import type { FormField, User } from '@/types';

export interface EventRegistrationTicket {
  id: string;
  name: string;
  price: number;
  enabled?: boolean;
  formSchema?: FormField[] | null;
  type: 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL';
  allowedAttendeeTypes?: string[];
}

export function isTicketEnabled(ticket: { enabled?: boolean }): boolean {
  return ticket.enabled !== false;
}

export interface EventRegistrationContext {
  isRegistered: boolean;
  isRegistrationClosed: boolean;
  isMember: boolean;
  endDate?: string | null;
  tickets: EventRegistrationTicket[];
}

export function isEventEnded(event: { endDate?: string | null }, now: Date = new Date()): boolean {
  if (!event.endDate) return false;
  return new Date(event.endDate).getTime() < now.getTime();
}

export type GoingRegistrationAction =
  | { type: 'instant'; ticket: EventRegistrationTicket }
  | { type: 'checkout'; ticket: EventRegistrationTicket }
  | { type: 'choose'; message: string };

function userHasUsableProfile(user: User): boolean {
  const displayName = [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ');
  const hasContact = Boolean(user.email?.trim() || user.phone?.trim());
  return Boolean(displayName.trim() && hasContact);
}

export function memberCanPurchaseTicket(
  ticket: EventRegistrationTicket,
  isMember: boolean
): boolean {
  const isMemberOnly = ticket.type === 'MEMBER_ONLY' || ticket.type === 'MEMBER_GUEST';
  return !isMemberOnly || isMember;
}

function ticketAllowsSelf(ticket: EventRegistrationTicket): boolean {
  return ticket.allowedAttendeeTypes?.includes('SELF') ?? false;
}

function ticketHasFormFields(ticket: EventRegistrationTicket): boolean {
  return Boolean(ticket.formSchema && ticket.formSchema.length > 0);
}

function isTicketSelfCheckoutEligible(
  ticket: EventRegistrationTicket,
  event: EventRegistrationContext
): boolean {
  if (!isTicketEnabled(ticket)) return false;
  if (!memberCanPurchaseTicket(ticket, event.isMember)) return false;
  if (!ticketAllowsSelf(ticket)) return false;
  return true;
}

function isTicketInstantSelfEligible(
  ticket: EventRegistrationTicket,
  event: EventRegistrationContext,
  user: User
): boolean {
  if (ticket.price !== 0) return false;
  if (ticketHasFormFields(ticket)) return false;
  if (!isTicketSelfCheckoutEligible(ticket, event)) return false;
  if (!userHasUsableProfile(user)) return false;
  return true;
}

export function getSelfRegisterTicket(
  event: EventRegistrationContext
): EventRegistrationTicket | null {
  if (event.isRegistered) return null;
  if (event.isRegistrationClosed) return null;

  const eligible = event.tickets.filter((ticket) => isTicketSelfCheckoutEligible(ticket, event));
  if (eligible.length !== 1) return null;
  return eligible[0];
}

export function getInstantSelfRegisterTicket(
  event: EventRegistrationContext,
  user: User | null | undefined
): EventRegistrationTicket | null {
  if (!user) return null;
  if (event.isRegistered) return null;
  if (event.isRegistrationClosed) return null;
  if (!userHasUsableProfile(user)) return null;

  const eligible = event.tickets.filter((ticket) =>
    isTicketInstantSelfEligible(ticket, event, user)
  );
  if (eligible.length !== 1) return null;
  return eligible[0];
}

export function canInstantSelfRegister(
  event: EventRegistrationContext,
  user: User | null | undefined
): boolean {
  return getInstantSelfRegisterTicket(event, user) !== null;
}

export function shouldShowQuickRegistrationUI(
  event: EventRegistrationContext,
  user: User | null | undefined
): boolean {
  if (event.isRegistered) return false;
  if (event.isRegistrationClosed) return false;
  if (isEventEnded(event)) return false;
  if (canInstantSelfRegister(event, user)) return true;
  if (getSelfRegisterTicket(event)) return true;
  return event.tickets.length === 1;
}

export function resolveGoingRegistrationAction(
  event: EventRegistrationContext,
  user: User | null | undefined
): GoingRegistrationAction | null {
  if (!user) return null;
  if (event.isRegistrationClosed) return null;

  const instantTicket = getInstantSelfRegisterTicket(event, user);
  if (instantTicket) {
    return { type: 'instant', ticket: instantTicket };
  }

  const selfTicket = getSelfRegisterTicket(event);
  if (selfTicket) {
    return { type: 'checkout', ticket: selfTicket };
  }

  if (event.tickets.length === 1) {
    const ticket = event.tickets[0];
    if (memberCanPurchaseTicket(ticket, event.isMember)) {
      return { type: 'checkout', ticket };
    }
    return {
      type: 'choose',
      message: 'You must be a member of this community to register for this event.',
    };
  }

  return { type: 'choose', message: 'Select a ticket to register for this event.' };
}

export function isGoingIntent(search: string): boolean {
  const params = new URLSearchParams(search);
  const going = params.get('going');
  return going === '1' || going === 'true';
}

export function stripGoingFromPath(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete('going');
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}
